/**
 * The maintenance bench: one aircraft, everything that fixes it.
 *
 * Until this existed the diagnostics, calibration, motor-test and parameter
 * routes were reachable only by hand-writing HTTP, which is the same as not
 * having shipped them.
 *
 * Scoped to one aircraft on purpose. Everything here is a property of a
 * specific airframe — this compass, these ESCs, this parameter set — and a
 * fleet-wide view of it would invite the one mistake that matters: calibrating
 * or spinning a motor on the drone that happened to be selected rather than the
 * one on the bench. The callsign is in the header of every tab.
 *
 * UNKNOWN is rendered as its own thing, never as OK. A sensor the aircraft has
 * said nothing about is not a healthy sensor, and the grey is deliberately not
 * green.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { getDiagnostics, type DiagnosticLevel, type DiagnosticsReport } from '@/api/diagnostics';
import { CalibrationRunner } from './CalibrationRunner';
import { ForwardingPanel } from './ForwardingPanel';
import { MotorTestPanel } from './MotorTestPanel';
import { ParameterBrowser } from './ParameterBrowser';
import { useDroneIdentity, useOwnDroneIds, usePrimarySelectedId } from '@/stores/droneStore';
import type { DroneId } from '@/types/domain';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';
const CARD = 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))';

const REFRESH_MS = 2000;

const LEVEL_COLOR: Record<DiagnosticLevel, string> = {
  ok: OK,
  warn: WARN,
  fail: DANGER,
  // Grey, and never green: a sensor nobody has heard from is not a healthy one.
  unknown: MUTED,
};

const LEVEL_LABEL: Record<DiagnosticLevel, string> = {
  ok: 'OK',
  warn: 'WARN',
  fail: 'FAIL',
  unknown: 'NO DATA',
};

type Tab = 'diagnostics' | 'calibration' | 'motors' | 'parameters' | 'forwarding';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'diagnostics', label: 'Diagnostics' },
  { id: 'calibration', label: 'Calibration' },
  { id: 'motors', label: 'Motor test' },
  { id: 'parameters', label: 'Parameters' },
  { id: 'forwarding', label: 'Forwarding' },
];

export function MaintenancePanel(): ReactNode {
  // Stable selectors only (useOwnDroneIds compares shallowly): a selector that
  // returns a new reference every snapshot re-renders this whole bench on every
  // telemetry tick, and telemetry arrives at 10 Hz. Other teams' aircraft are
  // left out, since the server refuses their diagnostics.
  const droneIds = useOwnDroneIds();
  const selectedFirst = usePrimarySelectedId();
  const [droneId, setDroneId] = useState<DroneId | null>(null);
  const [tab, setTab] = useState<Tab>('diagnostics');

  // Follows the map selection when there is one, so an operator who clicked an
  // aircraft and opened this panel is looking at that aircraft. Falls back to
  // the first known drone, and to nothing at all when the fleet is empty.
  const effective = droneId ?? selectedFirst ?? droneIds[0] ?? null;

  useEffect(() => {
    // A drone that disconnects while its bench is open must not leave the panel
    // driving commands at an id the registry no longer has.
    if (droneId !== null && !droneIds.includes(droneId)) setDroneId(null);
  }, [droneId, droneIds]);

  if (effective === null) {
    return (
      <p className="text-[11px]" style={{ color: MUTED }}>
        No aircraft connected. Connect one to run diagnostics or a calibration.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5">
          <span className="label-mono text-[9px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
            AIRCRAFT
          </span>
          <select
            value={effective}
            aria-label="Aircraft to service"
            onChange={(e) => setDroneId(e.target.value as DroneId)}
            className="rounded-[8px] border bg-transparent px-2 py-1 text-[11px]"
            style={{ borderColor: 'rgba(255,255,255,0.18)', color: '#f4f4f5' }}
          >
            {droneIds.map((id) => (
              <AircraftOption key={id} droneId={id} />
            ))}
          </select>
        </label>

        <div className="ml-auto flex max-w-full gap-1 overflow-x-auto [scrollbar-width:none]">
          {TABS.map((entry) => {
            const isActive = tab === entry.id;
            return (
              <button
                key={entry.id}
                type="button"
                aria-current={isActive ? 'true' : undefined}
                onClick={() => setTab(entry.id)}
                className="shrink-0 whitespace-nowrap rounded-[99px] px-3 py-[5px] text-[11px] transition-colors"
                style={{
                  background: isActive ? 'oklch(1 0 0 / 0.9)' : 'transparent',
                  color: isActive ? '#0c0c0d' : 'rgba(244,244,245,0.6)',
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {entry.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'diagnostics' && <DiagnosticsReportView droneId={effective} />}
      {tab === 'calibration' && <CalibrationRunner droneId={effective} />}
      {tab === 'motors' && <MotorTestPanel droneId={effective} />}
      {tab === 'parameters' && <ParameterBrowser droneId={effective} />}
      {tab === 'forwarding' && <ForwardingPanel droneId={effective} />}
    </div>
  );
}

/** Its own component so one aircraft's callsign changing does not re-render the
 * whole bench, and so the list never builds an object in a store selector. */
function AircraftOption({ droneId }: { droneId: DroneId }): ReactNode {
  const identity = useDroneIdentity(droneId);
  return (
    <option value={droneId} style={{ background: '#161617' }}>
      {identity?.callsign ?? droneId}
    </option>
  );
}

function DiagnosticsReportView({ droneId }: { droneId: DroneId }): ReactNode {
  const [report, setReport] = useState<DiagnosticsReport | null>(null);
  const [reachable, setReachable] = useState(true);

  useEffect(() => {
    let live = true;
    const load = () =>
      void getDiagnostics(droneId).then((r) => {
        if (!live) return;
        setReachable(r.ok);
        if (r.ok) setReport(r.data);
      });
    setReport(null);
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      live = false;
      window.clearInterval(id);
    };
  }, [droneId]);

  if (!reachable) {
    return (
      <p className="text-[11px]" style={{ color: WARN }}>
        Diagnostics unavailable for this aircraft.
      </p>
    );
  }
  if (!report) {
    return (
      <p className="text-[11px]" style={{ color: MUTED }}>
        Reading…
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div
        className="flex items-center gap-2.5 rounded-[12px] border px-3 py-2.5"
        style={{
          background: 'rgba(255,255,255,0.04)',
          borderColor: `color-mix(in oklch, ${LEVEL_COLOR[report.level]} 40%, transparent)`,
        }}
      >
        <span
          className="rounded-[99px] px-2 py-0.5 label-mono text-[9px]"
          style={{
            color: LEVEL_COLOR[report.level],
            background: `color-mix(in oklch, ${LEVEL_COLOR[report.level]} 16%, transparent)`,
          }}
        >
          {LEVEL_LABEL[report.level]}
        </span>
        <span className="text-[12px]" style={{ color: '#f4f4f5' }}>
          {report.summary}
        </span>
      </div>

      <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        {report.sections.map((section) => (
          <div
            key={section.name}
            className="rounded-[14px] border px-3.5 py-3"
            style={{ background: CARD, borderColor: 'rgba(255,255,255,0.13)' }}
          >
            <div className="mb-2 flex items-center gap-2">
              <span className="label-mono text-[9.5px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
                {section.name.toUpperCase()}
              </span>
              <span
                className="ml-auto size-[7px] rounded-full"
                style={{ background: LEVEL_COLOR[section.level] }}
                title={LEVEL_LABEL[section.level]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              {section.findings.length === 0 ? (
                <p className="text-[11px]" style={{ color: MUTED }}>
                  Nothing reported.
                </p>
              ) : (
                section.findings.map((finding) => (
                  <div key={finding.key} className="flex items-start gap-2 text-[11px]">
                    <span
                      className="mt-[5px] size-[6px] shrink-0 rounded-full"
                      style={{ background: LEVEL_COLOR[finding.level] }}
                    />
                    <span className="shrink-0" style={{ color: 'rgba(244,244,245,0.85)', minWidth: 96 }}>
                      {finding.subject}
                    </span>
                    <span style={{ color: MUTED }}>{finding.detail}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
